import { useState, useEffect, useRef } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  UserPlus, 
  Download, 
  Upload,
  Building2,
  Mail,
  Phone,
  MapPin,
  Users,
  KeyRound,
  Check,
  X,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { generateDirectoryPDF, generateDirectoryExcel } from '../utils/generateDirectoryExport';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERSON_TYPES = [
  { value: 'propietario', label: 'Propietario' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'facility_manager', label: 'Facility Manager' },
  { value: 'pagos', label: 'Pagos' },
  { value: 'contabilidad', label: 'Contabilidad' },
  { value: 'encargado', label: 'Encargado' },
  { value: 'desarrollador', label: 'Desarrollador' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'otro', label: 'Otro' },
  { value: '__personalizado__', label: 'Personalizado...' }
];

const PREDEFINED_TYPES = PERSON_TYPES.filter(t => t.value !== '__personalizado__').map(t => t.value);

const Directorio = () => {
  const { selectedPark } = usePark();
  const [persons, setPersons] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showLotsModal, setShowLotsModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipo: 'otro',
    empresa: '',
    notas: '',
    todos_los_lotes: false
  });
  
  // Lots assignment state
  const [selectedLots, setSelectedLots] = useState([]);
  const [allLotsSelected, setAllLotsSelected] = useState(false);
  
  // Create user state
  const [newPassword, setNewPassword] = useState('');
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, person: null });
  
  // Custom type state
  const [customTipo, setCustomTipo] = useState('');
  const [isCustomTipo, setIsCustomTipo] = useState(false);
  
  // CSV import
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    }
  }, [selectedPark]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [personsRes, lotsRes] = await Promise.all([
        axios.get(`${API}/directory?park_id=${selectedPark.id}`),
        axios.get(`${API}/lots?park_id=${selectedPark.id}`)
      ]);
      setPersons(personsRes.data);
      setLots(lotsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar el directorio');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      tipo: 'otro',
      empresa: '',
      notas: '',
      todos_los_lotes: false
    });
    setEditingPerson(null);
    setIsCustomTipo(false);
    setCustomTipo('');
  };

  const handleOpenPersonModal = (person = null) => {
    if (person) {
      setEditingPerson(person);
      const personTipo = person.tipo || 'otro';
      const isPredefined = PREDEFINED_TYPES.includes(personTipo);
      setFormData({
        nombre: person.nombre || '',
        email: person.email || '',
        telefono: person.telefono || '',
        tipo: isPredefined ? personTipo : '__personalizado__',
        empresa: person.empresa || '',
        notas: person.notas || '',
        todos_los_lotes: person.todos_los_lotes || false
      });
      setIsCustomTipo(!isPredefined);
      setCustomTipo(!isPredefined ? personTipo : '');
    } else {
      resetForm();
    }
    setShowPersonModal(true);
  };

  const handleSavePerson = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const finalTipo = isCustomTipo ? customTipo.trim() : formData.tipo;
    if (isCustomTipo && !finalTipo) {
      toast.error('Ingresa el tipo de persona');
      return;
    }

    try {
      const payload = {
        ...formData,
        tipo: finalTipo,
        park_id: selectedPark.id
      };

      if (editingPerson) {
        await axios.put(`${API}/directory/${editingPerson.id}`, payload);
        toast.success('Persona actualizada');
      } else {
        await axios.post(`${API}/directory`, payload);
        toast.success('Persona agregada al directorio');
      }
      
      setShowPersonModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving person:', error);
      toast.error('Error al guardar');
    }
  };

  const handleDeletePerson = async () => {
    if (!deleteConfirm.person) return;
    
    try {
      await axios.delete(`${API}/directory/${deleteConfirm.person.id}`);
      toast.success('Persona eliminada del directorio');
      setDeleteConfirm({ open: false, person: null });
      fetchData();
    } catch (error) {
      console.error('Error deleting person:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleOpenLotsModal = (person) => {
    setSelectedPerson(person);
    setAllLotsSelected(person.todos_los_lotes || false);
    setSelectedLots(person.lotes_asignados || []);
    setShowLotsModal(true);
  };

  const handleSaveLots = async () => {
    if (!selectedPerson) return;

    try {
      // First update the todos_los_lotes flag
      await axios.put(`${API}/directory/${selectedPerson.id}`, {
        ...selectedPerson,
        todos_los_lotes: allLotsSelected
      });

      // Then update lot assignments (only if not "all lots")
      if (!allLotsSelected) {
        await axios.post(`${API}/directory/${selectedPerson.id}/assign-lots`, selectedLots);
      }

      toast.success('Lotes asignados correctamente');
      setShowLotsModal(false);
      fetchData();
    } catch (error) {
      console.error('Error assigning lots:', error);
      toast.error('Error al asignar lotes');
    }
  };

  const handleOpenCreateUserModal = (person) => {
    if (!person.email) {
      toast.error('La persona debe tener un email para crear una cuenta');
      return;
    }
    setSelectedPerson(person);
    setNewPassword('');
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.post(`${API}/directory/${selectedPerson.id}/create-user?password=${encodeURIComponent(newPassword)}`);
      toast.success('Usuario creado exitosamente');
      setShowCreateUserModal(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const downloadTemplate = () => {
    const template = `nombre,email,telefono,tipo,empresa,notas
Juan Pérez,juan@ejemplo.com,5551234567,propietario,Empresa ABC,Propietario del lote A1
María García,maria@ejemplo.com,5559876543,inquilino,Comercializadora XYZ,Inquilino principal
Carlos López,carlos@ejemplo.com,5555555555,encargado,Servicios FM,Encargado general
Ana Torres,ana@ejemplo.com,5551112233,contabilidad,Despacho Contable,Contadora del parque`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_directorio.csv';
    link.click();
    toast.success('Plantilla descargada');
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/directory/import-csv?park_id=${selectedPark.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`${response.data.imported} personas importadas`);
      if (response.data.errors?.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }
      fetchData();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Error al importar CSV');
    }
    
    e.target.value = '';
  };

  const getTypeLabel = (tipo) => {
    const found = PERSON_TYPES.find(t => t.value === tipo);
    if (found) return found.label;
    // Capitalize first letter for custom types
    return tipo ? tipo.charAt(0).toUpperCase() + tipo.slice(1) : tipo;
  };

  const getTypeColor = (tipo) => {
    switch (tipo) {
      case 'propietario': return 'bg-blue-100 text-blue-800';
      case 'inquilino': return 'bg-green-100 text-green-800';
      case 'facility_manager': return 'bg-purple-100 text-purple-800';
      case 'pagos': return 'bg-amber-100 text-amber-800';
      case 'contabilidad': return 'bg-cyan-100 text-cyan-800';
      case 'encargado': return 'bg-orange-100 text-orange-800';
      case 'desarrollador': return 'bg-indigo-100 text-indigo-800';
      case 'proveedor': return 'bg-teal-100 text-teal-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getLotLabel = (person) => {
    if (person.todos_los_lotes) {
      return <span className="text-amber-600 font-medium">Todos los lotes</span>;
    }
    const count = person.lotes_asignados?.length || 0;
    if (count === 0) return <span className="text-slate-400">Sin lotes</span>;
    if (count === 1) {
      const lot = lots.find(l => l.id === person.lotes_asignados[0]);
      return lot?.numero_lote || '1 lote';
    }
    return `${count} lotes`;
  };

  // Filter persons
  const filteredPersons = persons.filter(person => {
    const matchesSearch = 
      person.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.empresa?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || person.tipo === filterType;
    
    return matchesSearch && matchesType;
  });

  if (!selectedPark) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Selecciona un parque para ver el directorio</p>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="directorio-title">Directorio de Personas</h1>
        <p>{selectedPark?.nombre}</p>
      </div>

      {/* Actions Bar */}
      <div className="card-industrial mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, email o empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 input-industrial"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 input-industrial" data-testid="filter-type">
              <SelectValue placeholder="Filtrar por tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {PERSON_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportCSV}
              accept=".csv"
              className="hidden"
            />
            <Button
              variant="outline"
              className="rounded-none"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
            >
              <Download size={16} className="mr-2" />
              Plantilla
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => fileInputRef.current?.click()}
              data-testid="import-csv-btn"
            >
              <Upload size={16} className="mr-2" />
              Importar
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => {
                generateDirectoryPDF(selectedPark.nombre, filteredPersons, lots);
                toast.success('PDF generado exitosamente');
              }}
              disabled={filteredPersons.length === 0}
              data-testid="export-pdf-btn"
            >
              <FileText size={16} className="mr-2" />
              PDF
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => {
                generateDirectoryExcel(selectedPark.nombre, filteredPersons, lots);
                toast.success('Excel generado exitosamente');
              }}
              disabled={filteredPersons.length === 0}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet size={16} className="mr-2" />
              Excel
            </Button>
            <Button 
              className="btn-primary rounded-none"
              onClick={() => handleOpenPersonModal()}
              data-testid="add-person-btn"
            >
              <Plus size={16} className="mr-2" />
              Agregar Persona
            </Button>
          </div>
        </div>
      </div>

      {/* Directory List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Cargando...</div>
        </div>
      ) : filteredPersons.length === 0 ? (
        <div className="card-industrial text-center py-12">
          <Users className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">
            {searchTerm || filterType !== 'all' ? 'No se encontraron resultados' : 'Directorio vacío'}
          </h3>
          <p className="text-slate-500 mb-4">
            {searchTerm || filterType !== 'all' 
              ? 'Intenta con otros filtros de búsqueda'
              : 'Agrega personas al directorio para comenzar'}
          </p>
          {!searchTerm && filterType === 'all' && (
            <Button onClick={() => handleOpenPersonModal()} className="btn-primary rounded-none">
              <Plus size={16} className="mr-2" />
              Agregar Primera Persona
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPersons.map(person => (
            <div key={person.id} className="card-industrial hover:border-slate-300 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-800" data-testid={`person-name-${person.id}`}>
                    {person.nombre}
                  </h3>
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded mt-1 ${getTypeColor(person.tipo)}`}>
                    {getTypeLabel(person.tipo)}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenLotsModal(person)}
                    className="h-8 w-8 p-0"
                    title="Asignar lotes"
                    data-testid={`assign-lots-btn-${person.id}`}
                  >
                    <MapPin size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenCreateUserModal(person)}
                    className="h-8 w-8 p-0"
                    title="Crear cuenta de usuario"
                    data-testid={`create-user-btn-${person.id}`}
                  >
                    <KeyRound size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenPersonModal(person)}
                    className="h-8 w-8 p-0"
                    data-testid={`edit-btn-${person.id}`}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirm({ open: true, person })}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    data-testid={`delete-btn-${person.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {person.email && (
                  <div className="flex items-center text-slate-600">
                    <Mail size={14} className="mr-2 text-slate-400" />
                    {person.email}
                  </div>
                )}
                {person.telefono && (
                  <div className="flex items-center text-slate-600">
                    <Phone size={14} className="mr-2 text-slate-400" />
                    {person.telefono}
                  </div>
                )}
                {person.empresa && (
                  <div className="flex items-center text-slate-600">
                    <Building2 size={14} className="mr-2 text-slate-400" />
                    {person.empresa}
                  </div>
                )}
                <div className="flex items-center text-slate-600">
                  <MapPin size={14} className="mr-2 text-slate-400" />
                  {getLotLabel(person)}
                </div>
              </div>
              
              {person.notas && (
                <p className="mt-3 text-xs text-slate-500 border-t pt-3">{person.notas}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Person Modal */}
      <Dialog open={showPersonModal} onOpenChange={setShowPersonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPerson ? 'Editar Persona' : 'Nueva Persona'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre completo"
                className="input-industrial"
                data-testid="person-nombre-input"
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                className="input-industrial"
                data-testid="person-email-input"
              />
            </div>
            
            <div>
              <Label>Teléfono</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="55 1234 5678"
                className="input-industrial"
                data-testid="person-telefono-input"
              />
            </div>
            
            <div>
              <Label>Tipo</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => {
                  setFormData({ ...formData, tipo: v });
                  if (v === '__personalizado__') {
                    setIsCustomTipo(true);
                  } else {
                    setIsCustomTipo(false);
                    setCustomTipo('');
                  }
                }}
              >
                <SelectTrigger className="input-industrial" data-testid="person-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERSON_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isCustomTipo && (
                <Input
                  value={customTipo}
                  onChange={(e) => setCustomTipo(e.target.value)}
                  placeholder="Escribe el tipo de persona..."
                  className="input-industrial mt-2"
                  data-testid="person-tipo-custom-input"
                  autoFocus
                />
              )}
            </div>
            
            <div>
              <Label>Empresa / Razón Social</Label>
              <Input
                value={formData.empresa}
                onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                placeholder="Nombre de la empresa"
                className="input-industrial"
                data-testid="person-empresa-input"
              />
            </div>
            
            <div>
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas adicionales..."
                className="input-industrial"
                rows={2}
                data-testid="person-notas-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPersonModal(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button onClick={handleSavePerson} className="btn-primary rounded-none" data-testid="save-person-btn">
              {editingPerson ? 'Guardar Cambios' : 'Agregar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lots Assignment Modal */}
      <Dialog open={showLotsModal} onOpenChange={setShowLotsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Asignar Lotes a {selectedPerson?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* All lots toggle */}
            <div className="flex items-center space-x-2 mb-4 p-3 bg-amber-50 border border-amber-200">
              <Checkbox
                id="all-lots"
                checked={allLotsSelected}
                onCheckedChange={(checked) => {
                  setAllLotsSelected(checked);
                  if (checked) {
                    setSelectedLots([]);
                  }
                }}
                data-testid="all-lots-checkbox"
              />
              <label htmlFor="all-lots" className="text-sm font-medium cursor-pointer">
                Asignar TODOS los lotes del parque
              </label>
            </div>
            
            {/* Individual lots */}
            {!allLotsSelected && (
              <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
                {lots.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No hay lotes en este parque</p>
                ) : (
                  lots.map(lot => (
                    <div key={lot.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50">
                      <Checkbox
                        id={`lot-${lot.id}`}
                        checked={selectedLots.includes(lot.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedLots([...selectedLots, lot.id]);
                          } else {
                            setSelectedLots(selectedLots.filter(id => id !== lot.id));
                          }
                        }}
                        data-testid={`lot-checkbox-${lot.id}`}
                      />
                      <label htmlFor={`lot-${lot.id}`} className="flex-1 text-sm cursor-pointer">
                        <span className="font-medium">{lot.numero_lote}</span>
                        {lot.propietario_nombre && (
                          <span className="text-slate-500 ml-2">({lot.propietario_nombre})</span>
                        )}
                      </label>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {!allLotsSelected && selectedLots.length > 0 && (
              <p className="text-sm text-slate-500 mt-2">
                {selectedLots.length} lote(s) seleccionado(s)
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLotsModal(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button onClick={handleSaveLots} className="btn-primary rounded-none" data-testid="save-lots-btn">
              Guardar Asignación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Cuenta de Usuario</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-50 p-4 rounded text-sm">
              <p><strong>Nombre:</strong> {selectedPerson?.nombre}</p>
              <p><strong>Email:</strong> {selectedPerson?.email}</p>
              <p className="mt-2 text-slate-500">
                Se creará una cuenta de usuario tipo "Consulta" que podrá acceder a la información
                del parque y los lotes asignados.
              </p>
            </div>
            
            <div>
              <Label>Contraseña para el usuario *</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="input-industrial"
                data-testid="new-password-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserModal(false)} className="rounded-none">
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} className="btn-primary rounded-none" data-testid="confirm-create-user-btn">
              <UserPlus size={16} className="mr-2" />
              Crear Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, person: null })}
        onConfirm={handleDeletePerson}
        title="Eliminar Persona"
        description={`¿Estás seguro de eliminar a "${deleteConfirm.person?.nombre}" del directorio? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="destructive"
      />
    </div>
  );
};

export default Directorio;
